/**
 * Appels et clés de cache du centre de notifications.
 *
 * Schémas importés de `@afs/shared` — les mêmes que ceux avec lesquels l'API
 * valide. Dans `shared/` et non dans un module : la cloche apparaît dans la
 * coque de chaque module, elle n'appartient à aucun
 * (skill `frontend-react-afs` § Structure).
 */
import {
  schemaPageNotifications,
  schemaResumeNotifications,
  type FiltresNotifications,
  type ResumeNotifications,
} from '@afs/shared'
import { z } from 'zod'
import { requete } from '../api/client.js'

type PageNotifications = z.infer<typeof schemaPageNotifications>

export const clesNotifications = {
  tout: ['notifications'] as const,
  /** Le compteur de la cloche et ses dernières lignes. */
  resume: () => [...clesNotifications.tout, 'resume'] as const,
  /**
   * Les filtres font partie de la clé : sans eux, passer de « non lues » à
   * « toutes » afficherait le cache de la requête précédente.
   */
  liste: (filtres: FiltresNotifications) => [...clesNotifications.tout, 'liste', filtres] as const,
}

export function resumeNotifications(): Promise<ResumeNotifications> {
  return requete('/notifications/resume', { schema: schemaResumeNotifications })
}

export function listerNotifications(filtres: FiltresNotifications): Promise<PageNotifications> {
  const parametres = new URLSearchParams({
    page: String(filtres.page),
    limite: String(filtres.limite),
  })
  if (filtres.nonLues !== undefined) parametres.set('nonLues', String(filtres.nonLues))

  return requete(`/notifications?${parametres.toString()}`, { schema: schemaPageNotifications })
}

/**
 * Marque comme lu et rend le compteur À JOUR.
 *
 * Sans identifiants, tout est marqué — c'est « Tout marquer comme lu ». Le
 * serveur rend le nouveau compteur, que la cloche affiche directement : rien
 * à recalculer, rien à redemander.
 */
export function marquerNotificationsLues(ids?: readonly string[]): Promise<{ nonLues: number }> {
  return requete('/notifications/lues', {
    methode: 'POST',
    corps: ids === undefined ? {} : { ids },
    schema: z.object({ nonLues: z.number().int() }),
  })
}
